import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/components/EntityHistory";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Upload } from "lucide-react";

const HELP = {
  restock: "Shopify > App > SW Back in Stock > Richieste > Esporta",
  wishlist: "Shopify > App > Advanced Wishlist > Esporta CSV",
};

export const DemandUpload = ({ source, label, info }) => {
  const qc = useQueryClient();
  const ref = useRef();
  const done = () => qc.invalidateQueries({ queryKey: ["demand"] });
  const upload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/demand/import", fd, { params: { source } })).data;
    },
    onSuccess: (r) => {
      done();
      toast.success(`${label}: ${r.imported} nuove richieste, ${r.duplicates} già presenti${r.unmatched ? `, ${r.unmatched} non abbinate a un prodotto` : ""}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Caricamento non riuscito"),
  });
  const clear = useMutation({
    mutationFn: async () => (await api.delete("/demand", { params: { source } })).data,
    onSuccess: (r) => { done(); toast.success(`${r.deleted} richieste eliminate`); },
  });
  return (
    <Card className="flex flex-col gap-3 p-4" data-testid={`demand-upload-${source}`}>
      <div>
        <p className="font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">Esporta da: {HELP[source]}</p>
        <p className="mt-1 text-xs text-muted-foreground" data-testid={`demand-last-${source}`}>
          {info ? `Ultimo caricamento: ${fmtDateTime(info.at)}${info.by ? ` da ${info.by}` : ""}` : "Nessun file caricato"}
        </p>
      </div>
      <input ref={ref} type="file" accept=".csv,.xlsx" className="hidden" data-testid={`demand-file-${source}`}
        onChange={(e) => { if (e.target.files[0]) upload.mutate(e.target.files[0]); e.target.value = ""; }} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => ref.current.click()} disabled={upload.isPending} data-testid={`demand-upload-btn-${source}`}>
          {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Carica file CSV/Excel
        </Button>
        {info && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" data-testid={`demand-clear-${source}`}
            onClick={() => window.confirm(`Eliminare tutte le richieste caricate da ${label}?`) && clear.mutate()}>
            <Trash2 className="mr-1 h-4 w-4" />Svuota
          </Button>
        )}
      </div>
    </Card>
  );
};
